// User and Auth Schema
import * as Yup from "yup";
import { optionalPhoneSchema } from "../utils/phone";

// Auth Schema
export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("validation:partner.emailInvalid")
    .required("validation:auth.emailRequired"),
  password: Yup.string()
    .min(8, "validation:auth.passwordMin")
    .required("validation:auth.passwordRequired"),
});

export const forgotSchema = Yup.object().shape({
  email: Yup.string()
    .email("validation:partner.emailInvalid")
    .required("validation:auth.emailRequired"),
});

export const passwordSchema = Yup.object({
  password: Yup.string()
    .required("validation:auth.passwordRequired")
    .min(8, "validation:auth.passwordMin")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "validation:auth.passwordComplexity",
    ),
  confirmPassword: Yup.string()
    .required("validation:auth.confirmPasswordRequired")
    .oneOf([Yup.ref("password")], "validation:auth.passwordsMustMatch"),
});

export const resetPasswordSchema = Yup.object().shape({
  token: Yup.string().required("validation:auth.resetTokenRequired"),
  password: Yup.string()
    .min(8, "validation:auth.passwordMin")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      "validation:auth.passwordComplexity",
    )
    .required("validation:auth.passwordRequired"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "validation:auth.passwordsDoNotMatch")
    .required("validation:auth.confirmPasswordRequired"),
});

//User Schema
export const userSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("validation:user.firstNameRequired")
    .min(2, "validation:user.firstNameMin")
    .max(50, "validation:user.firstNameMax"),

  lastName: Yup.string()
    .required("validation:user.lastNameRequired")
    .min(2, "validation:user.lastNameMin")
    .max(50, "validation:user.lastNameMax"),

  email: Yup.string()
    .email("validation:partner.emailInvalid")
    .required("validation:user.emailRequired"),

  phone: optionalPhoneSchema("validation:user.phoneInvalid"),

  address: Yup.object().shape({
    street: Yup.string().nullable().notRequired(),
    city: Yup.string().nullable().notRequired(),
    state: Yup.string().nullable().notRequired(),
    zipcode: Yup.string()
      .nullable()
      .notRequired()
      .transform((value) => (value === "" ? null : value))
      .matches(/^\d{5}(-\d{4})?$/, "validation:user.zipcodeFormat"),

    postalcode: Yup.string().nullable().notRequired(),
    country: Yup.string()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === "") return null;
        return value;
      })
      .test(
        "is-valid-objectid",
        "validation:address.countryInvalid",
        (value) => {
          if (!value) return true;
          return /^[0-9a-fA-F]{24}$/.test(value);
        },
      ),
  }),
});

export const createUserSchema = Yup.object().shape({
  email: Yup.string()
    .email("validation:partner.emailInvalid")
    .required("validation:user.emailRequired"),
  firstName: Yup.string()
    .min(2, "validation:user.firstNameMin")
    .max(50, "validation:user.firstNameMax")
    .required("validation:user.firstNameRequired"),
  lastName: Yup.string()
    .min(2, "validation:user.lastNameMin")
    .max(50, "validation:user.lastNameMax")
    .required("validation:user.lastNameRequired"),
  role: Yup.string()
    .oneOf(["admin", "user", "driver"], "validation:user.roleInvalid")
    .default("user"),
  phone: optionalPhoneSchema("validation:user.phoneInvalid"),
  address: Yup.object()
    .shape({
      street: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zipcode: Yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .matches(/^\d{5}(-\d{4})?$/, "validation:user.zipcodeFormat"),

      postalcode: Yup.string().nullable(),
      country: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "") return null;
          return value;
        })
        .test(
          "is-valid-objectid",
          "validation:address.countryInvalid",
          (value) => {
            if (!value) return true;
            return /^[0-9a-fA-F]{24}$/.test(value);
          },
        ),
    })
    .nullable(),
});

export const updateUserSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "validation:user.firstNameMin")
    .max(50, "validation:user.firstNameMax"),
  lastName: Yup.string()
    .min(2, "validation:user.lastNameMin")
    .max(50, "validation:user.lastNameMax"),
  phone: optionalPhoneSchema("validation:user.phoneInvalid"),
  address: Yup.object()
    .shape({
      street: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zipcode: Yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .matches(/^\d{5}(-\d{4})?$/, "validation:user.zipcodeFormat"),
      postalcode: Yup.string().nullable(),
      country: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "") return null;
          return value;
        })
        .test(
          "is-valid-objectid",
          "validation:address.countryInvalid",
          (value) => {
            if (!value) return true;
            return /^[0-9a-fA-F]{24}$/.test(value);
          },
        ),
    })
    .nullable(),
});

export const updateUserStatusSchema = Yup.object().shape({
  status: Yup.string()
    .oneOf(["active", "inactive"], "validation:user.statusInvalid")
    .required("validation:user.statusRequired"),
});

export const updateUserRoleSchema = Yup.object().shape({
  role: Yup.string()
    .oneOf(["admin", "user", "driver"], "validation:user.roleInvalid")
    .required("validation:user.roleRequired"),
});

export const updateProfileSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "validation:user.firstNameMin")
    .max(50, "validation:user.firstNameMax")
    .nullable(),

  lastName: Yup.string()
    .min(2, "validation:user.lastNameMin")
    .max(50, "validation:user.lastNameMax")
    .nullable(),

  phone: optionalPhoneSchema("validation:user.phoneInvalid"),

  address: Yup.object()
    .shape({
      street: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zipcode: Yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .matches(/^\d{5}(-\d{4})?$/, "validation:user.zipcodeFormat"),
      postalcode: Yup.string().nullable(),
      country: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "") return null;
          return value;
        })
        .test(
          "is-valid-objectid",
          "validation:address.countryInvalid",
          (value) => {
            if (!value) return true;
            return /^[0-9a-fA-F]{24}$/.test(value);
          },
        ),
    })
    .nullable(),

  currentPassword: Yup.string()
    .nullable()
    .when("newPassword", {
      is: (val: string) => val && val.length > 0,
      then: (schema) =>
        schema.required("validation:auth.currentPasswordRequired"),
      otherwise: (schema) => schema.nullable(),
    }),

  newPassword: Yup.string()
    .nullable()
    .test(
      "password-requirements",
      "validation:auth.passwordRequirements",
      function (value) {
        if (!value) return true;

        const errors = [];

        if (value.length < 8) {
          errors.push("validation:auth.passwordMin");
        }
        if (!/[a-z]/.test(value)) {
          errors.push("validation:auth.passwordLowercase");
        }
        if (!/[A-Z]/.test(value)) {
          errors.push("validation:auth.passwordUppercase");
        }
        if (!/\d/.test(value)) {
          errors.push("validation:auth.passwordNumber");
        }
        if (!/[@$!%*?&]/.test(value)) {
          errors.push("validation:auth.passwordSpecial");
        }

        if (errors.length > 0) {
          return this.createError({ message: errors.join(", ") });
        }

        return true;
      },
    ),

  confirmNewPassword: Yup.string()
    .nullable()
    .when("newPassword", {
      is: (val: string) => val && val.length > 0,
      then: (schema) =>
        schema
          .required("validation:auth.confirmNewPasswordRequired")
          .oneOf(
            [Yup.ref("newPassword")],
            "validation:auth.confirmNewPasswordMismatch",
          ),
      otherwise: (schema) => schema.nullable(),
    }),
});
